"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "dark" | "light";

interface ThemeCtx {
  theme: Theme;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeCtx>({
  theme: "light",
  toggle: () => {},
});

export const useTheme = () => useContext(ThemeContext);

function resolveInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  try {
    const stored = localStorage.getItem("theme");
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    // localStorage unavailable (private browsing, etc.)
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function syncFavicon(theme: Theme) {
  if (typeof document === "undefined") return;

  const href = theme === "dark" ? "/favicon-dark.ico" : "/favicon-light.ico";
  const candidates = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');

  for (const candidate of candidates) {
    if (candidate.getAttribute("data-rrai-favicon") === "active") continue;

    const media = candidate.getAttribute("media");
    const candidateHref = candidate.getAttribute("href") ?? "";
    if (media) continue;

    const baseHref = candidateHref.split("?")[0]?.toLowerCase() || "";
    if (baseHref.endsWith("/favicon.ico")) {
      candidate.remove();
    }
  }

  let link = document.querySelector<HTMLLinkElement>('link[data-rrai-favicon="active"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "icon");
    link.setAttribute("type", "image/x-icon");
    link.setAttribute("data-rrai-favicon", "active");
    document.head.appendChild(link);
  }

  if (link.getAttribute("href") !== href) {
    link.setAttribute("href", href);
  }
}

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  syncFavicon(theme);
}

export default function ThemeProvider({ children }: { children: ReactNode }) {
  // SSR default is "light" to match the html attribute; corrected on mount
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    const initial = resolveInitialTheme();
    // Intentional one-time sync from pre-hydration theme script/localStorage.
    // We keep SSR default stable and align after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTheme(initial);
    applyTheme(initial);

    // Follow OS preference changes when no user preference is stored
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      try {
        if (localStorage.getItem("theme")) return;
      } catch {}
      const next: Theme = media.matches ? "dark" : "light";
      setTheme(next);
      applyTheme(next);
    };

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (media as any).addListener(onChange);
    }
    return () => {
      if (typeof media.removeEventListener === "function") {
        media.removeEventListener("change", onChange);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (media as any).removeListener(onChange);
      }
    };
  }, []);

  const toggle = () => {
    setTheme((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem("theme", next);
      } catch {}
      applyTheme(next);
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}
